import { browserHistory } from 'react-router';

import { get, includes, isEmpty } from 'lodash';
import querystring from 'query-string';
import { delay } from 'redux-saga';
import { call, CallEffect, put, select } from 'redux-saga/effects';
import { sendLog, LoggerStatusEnum } from 'ui-model';

import { changeLocale, initApp, showMessage, signOut } from '@lib/actions';
import { FETCH_CONTEXT, LOGIN_WIDGET_USER } from '@lib/actions/actionTypes';
import { normalizeLocale } from '@lib/actions/changeLocale';
import save, { sagaSave } from '@lib/actions/save';
import { patientFormsResource } from '@lib/resources';
import contextsResource from '@lib/resources/contextsResource';
import { RolloutEnum } from '@lib/resources/types';
import widgetUsersResource from '@lib/resources/widgetUsersResource';
import { sagaFetch, sagaValidateToken } from '@lib/sagas';
import { selectCurrentUser } from '@lib/selectors';
import storageService from '@lib/services/storageService';

import { registerSocket, updatePatientFormLinkingStatus } from 'containers/App/actions';
import { submitError } from 'containers/SignupPage/actions';
import { getTokenData } from 'utils/getTokenData';
import request from 'utils/request';

import sagaFetchCurrentUser from './sagaFetchCurrentUser';
import sagaFetchCustomWebStyles from './sagaFetchCustomWebStyles';

interface RetryPatientFormLinkingParams {
  pendingPatientFormId: string;
  currentUserId: string;
  patientForm: any;
  patientFormResponse: any;
  maxRetries?: number;
}

// Helper function to retry patient-form linking
function* linkPatientToFormWithRetries({
  pendingPatientFormId,
  currentUserId,
  patientForm,
  patientFormResponse,
  maxRetries = 3,
}: RetryPatientFormLinkingParams): any {
  const RETRY_DELAY_MS = 2000; // 2 seconds between retries

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    // Dispatch action to update Redux state with retry status
    yield put(
      updatePatientFormLinkingStatus({
        formId: pendingPatientFormId,
        patientId: currentUserId,
        status: 'retrying',
        attempt,
        maxRetries,
        timestamp: Date.now(),
      }),
    );

    try {
      const result = yield call(
        sagaSave,
        save.request({
          resource: patientFormsResource,
          data: {
            id: pendingPatientFormId,
            ...{ ...patientForm, ...patientFormResponse },
            patient: currentUserId,
          },
          meta: { ignoreErrors: false, skipMessages: true },
        }),
      );

      // Success! Dispatch success status
      yield put(
        updatePatientFormLinkingStatus({
          formId: pendingPatientFormId,
          patientId: currentUserId,
          status: 'success',
          timestamp: Date.now(),
        }),
      );

      // Clear status after a short delay
      yield delay(2000);
      yield put(updatePatientFormLinkingStatus(null));

      return { success: true, data: result };
    } catch (error) {
      sendLog(
        `Error on linkPatientToFormWithRetries - attempt ${attempt} of ${maxRetries} - pendingPatientFormId: ${pendingPatientFormId} - currentUserId: ${currentUserId}`,
        { errorStack: error },
        LoggerStatusEnum.error,
      );
      if (attempt === maxRetries) {
        // All retries exhausted - dispatch failed status
        yield put(
          updatePatientFormLinkingStatus({
            formId: pendingPatientFormId,
            patientId: currentUserId,
            status: 'failed',
            error: get(error, 'message', 'unknown'),
            timestamp: Date.now(),
          }),
        );
        return { success: false, error };
      }

      // Wait before next retry
      yield delay(RETRY_DELAY_MS * attempt); // Exponential backoff
    }
  }
}

// bind resource generator
const fetchContext = sagaFetch.bind(null, FETCH_CONTEXT, contextsResource.fetchContext);
const loginWidgetUser = sagaFetch.bind(null, LOGIN_WIDGET_USER, widgetUsersResource.login);

export default function* sagaInitApp(action: ReturnType<typeof initApp.request>): any {
  const {
    setParameters,
    get: storageGet,
    remove: storageRemove,
    setToken,
    getPatientFormPending,
    removePatientFormPending,
    getLocaleKeyByUserId,
  } = storageService;

  const {
    payload: { location, meta },
  } = action;
  // keep URL parameters on storage
  if (location && !isEmpty(location.query)) {
    const isReschedule = /reschedule/i.test(location.pathname);
    setParameters({
      reschedule: isReschedule,
      ...location.query,
    });
    // Clear localStorage if patient is rescheduling
    if (isReschedule && storageGet('patientFormId')) {
      storageRemove('patientFormId');
    }
  }

  // if user exists, login to get token

  // NOTE: some views are still using the token from the URL directly instead of getting it from the storage
  // which will cause problems in case we pass both: `user` and `token` URL parameters to those views
  // because they'll be using the token from the URL instead of the one from the user

  // FIXME: remove all appearances of location.query.token and use getToken when necessary
  const userId = get(location, 'query.user');
  const res = yield getTokenFromUserId(userId);

  if (res?.error) {
    yield put(signOut({}));
    return;
  }

  const token = action.payload.token || selectToken(res, userId);

  let roles = get(getTokenData(token), 'roles', []);

  if (!token) {
    yield put(signOut({}));
    return;
  }

  setToken(token);

  // skip fetching auth for some screens
  const pathname = location ? location.pathname : '';
  const skipAuth = /verify|telehealth/.test(pathname);

  if (!skipAuth) {
    let validatedToken: any = {};

    // validate token when is not a widget user
    if (!userId) {
      validatedToken = yield sagaValidateToken({ token });

      if (validatedToken.error) {
        yield put(signOut({}));
        return;
      }

      roles = get(validatedToken, 'data.roles', []);
    }

    // FIXME different patients might log into the app so we don't want to keep old IDs on the storage
    // First time the app loads all storage should be cleaned up/replaced
    if (includes(roles, 'widget')) {
      storageRemove('patientFormId');
    }

    if (!location || !/login/.test(location.pathname)) {
      // fetch current user
      yield sagaFetchCurrentUser();

      // fetch context
      // FIXME we want to fetch the context no matter if we're validating the token/fetching the user
      const refId = get(location, 'query.refId');
      if (refId) {
        yield call(fetchContext, { refId });
      }
    }
  }
  const currentUser = yield select(selectCurrentUser);

  let patientFormStarted;
  let patientFormLinkingFailed = false;
  if (currentUser) {
    // Set patient on pending patient forms
    // TODO use the `payload.location` passed inside action
    const searchParamsString = window.location.search;
    const searchParams = querystring.parse(searchParamsString);

    // TODO diegopessigatt avoid fetching and updating the patientForm each time we open the patient form page
    // This workaround should only be for the patient form ID that is marked as pending inside the local storage
    // Related: https://github.com/lumahealthhq/patient-web-app/commit/d1da9aad769b510b8f347da930101f0c5d054fbe
    const queryPatientFormId = get(searchParams, 'patientForm', null);

    const pendingPatientFormId = getPatientFormPending() || queryPatientFormId;
    const shouldLinkPatientForm =
      pendingPatientFormId && currentUser.type === 'patient' && !includes(roles, 'patientLoginOnly');

    if (shouldLinkPatientForm) {
      const patientForm = yield select(patientFormsResource.makeSelectEntity(pendingPatientFormId));
      let patientFormResponse = {};
      if (!patientForm) {
        patientFormResponse = yield call(request, `/patientForms/${pendingPatientFormId}`, { method: 'get' });
      }

      // Use retry mechanism for patient-form linking
      const linkingResult = yield call(linkPatientToFormWithRetries, {
        pendingPatientFormId,
        currentUserId: currentUser.id,
        patientForm,
        patientFormResponse,
        maxRetries: 3,
      });

      if (linkingResult.success) {
        patientFormStarted = linkingResult.data;
      } else {
        // All retries failed
        patientFormLinkingFailed = true;
        removePatientFormPending();
      }

      const linkedFormsStorage = storageService.get('lh:linked-forms');
      const patientFormIdStorage = storageService.get('patientFormId');
      const hasLinkedForm = linkedFormsStorage && patientFormIdStorage && linkedFormsStorage[patientFormIdStorage];
      if (!hasLinkedForm) {
        removePatientFormPending();
      }
    }
    if (!currentUser.$isWidgetUser) yield put(registerSocket(currentUser));
  }

  const isLumabot = /lumabot/.test(pathname);

  if (token && !isLumabot) {
    // update locale
    const locale = storageGet(getLocaleKeyByUserId(get(currentUser, 'id', ''))) || get(currentUser, 'language');
    yield put(changeLocale(normalizeLocale(locale)));

    // fetch theme
    yield sagaFetchCustomWebStyles({ payload: { id: get(location, 'query.customWebStylesId', null) } });
  }

  // app ready
  yield put(initApp.success({}));

  // Check if patient-form linking failed before redirecting
  if (patientFormLinkingFailed) {
    // Reset loading state and show error message
    yield put(submitError({ statusText: 'Unable to verify. Please try again.' }));
    yield put(showMessage('Unable to verify. Please try again.', 'error'));
    return; // Don't proceed with redirect
  }

  // callbackSuccess
  if (meta && meta.callbackSuccess) {
    const patientForm = patientFormStarted?.data;
    meta.callbackSuccess({ ...(res || {}), ...(patientForm && { patientForm }) });
  }

  // Go to the returnUrl once the app is ready
  if (currentUser?.rollouts?.includes(RolloutEnum.NextyPatientLogin)) {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl');
    if (returnUrl) {
      const newUrl = new URL(returnUrl, window.location.origin);
      newUrl.searchParams.delete('token');
      browserHistory.push(newUrl.href);
    }
  }
}

function* getTokenFromUserId(
  userId?: string,
): Generator<CallEffect, { data?: unknown; error?: unknown } | undefined, { data?: unknown; error?: unknown }> {
  if (!userId) return;
  return yield call(loginWidgetUser, { id: userId });
}

function selectToken(res?: unknown, userId?: string) {
  const tokenFromUserId = get(res, 'data.token', '');
  if (userId && tokenFromUserId) return tokenFromUserId;

  const tokenFromStorage = storageService.getToken();

  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromQuery = urlParams.get('token');

  return tokenFromQuery || tokenFromStorage;
}
